/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import type { IMonthItem } from '../../models/IMonthItem';
import {
  Button,
  Field,
  Input,
  makeStyles,
  tokens,
  Textarea,
  Label,
} from '@fluentui/react-components';
import { DataService } from '../../services/dataService';
import { useMsal } from '@azure/msal-react';

const useStyles = makeStyles({
  section: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground2, //'#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  fieldLabel: {
    fontWeight: '600',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2LinkSelected, //'#444',
    alignItems: 'center',
    paddingBottom: '5px',
    paddingTop: '10px',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '15px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
});

interface IEditMonthProps {
  selectedMonth: IMonthItem | null;
  onClose?: () => void;
}
const EditMonth: React.FC<IEditMonthProps> = ({ selectedMonth, onClose }) => {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [verbrauchterUrlaub, setVerbrauchterUrlaub] = React.useState<number>(
    selectedMonth ? selectedMonth.verbrauchterUrlaub : 0
  );
  const [urlaubMonatsende, setUrlaubMonatsende] = React.useState<number>(
    selectedMonth ? selectedMonth.urlaubsRest : 0
  );
  const [strMonatsUeberschuss, setStrMonatsUeberschuss] =
    React.useState<string>(
      selectedMonth ? selectedMonth.strMonatsUeberschuss : ''
    );
  const [strUeberStundenVorAbzug, setStrUeberStundenVorAbzug] =
    React.useState<string>(
      selectedMonth ? selectedMonth.strUeberStundenVorAbzug : ''
    );
  const [strUeberStundenAbzug, setStrUeberStundenAbzug] =
    React.useState<string>(
      selectedMonth ? selectedMonth.strUeberStundenAbzug : ''
    );
  const [strGleitzeitKonto, setStrGleitzeitKonto] = React.useState<string>(
    selectedMonth ? selectedMonth.strGleitzeitKonto : ''
  );
  const [strUeberStunden, setStrUeberStunden] = React.useState<string>(
    selectedMonth ? selectedMonth.strUeberStunden : ''
  );
  const [kommentar, setKommentar] = React.useState<string>(
    selectedMonth ? selectedMonth.kommentar : ''
  );

  const getUhrzeitDoubleFromString = (wert: string): number => {
    try {
      const zeit = wert.split(':');
      let uhrZeit = parseFloat(zeit[0]);
      if (zeit[0].startsWith('-')) {
        uhrZeit = uhrZeit - parseFloat(zeit[1]) / 60;
      } else {
        uhrZeit = uhrZeit + parseFloat(zeit[1]) / 60;
      }
      return uhrZeit;
    } catch {
      return 0;
    }
  };

  const getUhrzeitStringFromDouble = (wert: number | null): string => {
    if (wert !== null && wert !== undefined) {
      const st = wert;
      const dateMinuten = new Date(0);
      const Komma = st - Math.trunc(st);
      if (Komma > 0) {
        dateMinuten.setMinutes(Komma * 60);
      } else {
        dateMinuten.setMinutes(-Komma * 60);
      }

      if (st < 0 && Math.trunc(st) === 0) {
        return (
          '-' +
          String(Math.trunc(st)).padStart(2, '0') +
          ':' +
          String(dateMinuten.getMinutes()).padStart(2, '0')
        );
      } else {
        return (
          String(Math.trunc(st)).padStart(2, '0') +
          ':' +
          String(dateMinuten.getMinutes()).padStart(2, '0')
        );
      }
    } else return '00:00';
  };

  const onMonatsUeberschussChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStrMonatsUeberschuss(e.target.value);
    const value = getUhrzeitDoubleFromString(e.target.value);
    if (isNaN(value)) return;
    const ueberStundenVorAbzug =
      value + (selectedMonth?.ueberStundenVormonat || 0);
    setStrUeberStundenVorAbzug(
      getUhrzeitStringFromDouble(ueberStundenVorAbzug)
    );
    setStrUeberStunden(
      getUhrzeitStringFromDouble(
        ueberStundenVorAbzug - getUhrzeitDoubleFromString(strUeberStundenAbzug)
      )
    );
  };

  const onAbzugUeberstundenChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStrUeberStundenAbzug(e.target.value);
    const value = getUhrzeitDoubleFromString(e.target.value);
    if (isNaN(value)) return;
    const ueberstunden =
      getUhrzeitDoubleFromString(strUeberStundenVorAbzug) - value;
    setStrUeberStunden(getUhrzeitStringFromDouble(ueberstunden));
  };

  const saveMonth = async () => {
    setErrorMessage('');
    if (!dataService) return;
    if (!selectedMonth) {
      setErrorMessage('Kein Monat ausgewählt');
      return;
    }
    await dataService
      .updateMonthItem({
        ...selectedMonth,
        verbrauchterUrlaub: verbrauchterUrlaub,
        urlaubsRest: urlaubMonatsende,
        monatsUeberschuss: getUhrzeitDoubleFromString(strMonatsUeberschuss),
        ueberStundenAbzug: getUhrzeitDoubleFromString(strUeberStundenAbzug),
        ueberStunden: getUhrzeitDoubleFromString(strUeberStunden),
        gleitzeitKonto: getUhrzeitDoubleFromString(strGleitzeitKonto),
        kommentar: kommentar,
      } as IMonthItem)
      .then((_result) => {
        setErrorMessage('Daten erfolgreich gespeichert');
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 3000);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage('Fehler beim Speichern des Monats');
      });
  };

  const _sendClicked = () => {
    void saveMonth();
  };

  return (
    <>
      {errorMessage && (
        <div
          style={{
            fontSize: '16px',
            marginBottom: '30px',
            maxWidth: '500px',
            color: 'red',
          }}
        >
          {errorMessage}
        </div>
      )}
      <hr
        style={{
          height: '2px',
          borderWidth: 0,
          color: 'gray',
          backgroundColor: 'gray',
        }}
      />
      <div className={styles.section}>
        <Field label="Urlaub Vormonat" className={styles.fieldLabel}>
          <Label className="anzeigeLabel">
            {selectedMonth ? selectedMonth.urlaubVormonat : 0} Tage
          </Label>
        </Field>
        <Field label="Verbrauchter Urlaub" className={styles.fieldLabel}>
          <Input
            value={verbrauchterUrlaub.toString()}
            onChange={(e) => setVerbrauchterUrlaub(parseInt(e.target.value))}
          />
        </Field>
        <Field label="Urlaub Monatsende" className={styles.fieldLabel}>
          <Input
            value={urlaubMonatsende.toString()}
            onChange={(e) => setUrlaubMonatsende(parseInt(e.target.value))}
          />
        </Field>
        <Field label="Überstunden Vormonat" className={styles.fieldLabel}>
          <Label className="anzeigeLabel">
            {selectedMonth ? selectedMonth.strUeberStundenVormonat : '0:00'}
          </Label>
        </Field>
        <Field label="Monatsüberschuss" className={styles.fieldLabel}>
          <Input
            value={strMonatsUeberschuss}
            onChange={onMonatsUeberschussChange}
          />
        </Field>
        <Field
          label="Überstunden (Stand Ende des Monats)"
          className={styles.fieldLabel}
        >
          <Label className="anzeigeLabel">{strUeberStundenVorAbzug}</Label>
        </Field>
        <Field
          label="Abzug Überstunden (ÜP, Auszahlung)"
          className={styles.fieldLabel}
        >
          <Input
            value={strUeberStundenAbzug}
            onChange={onAbzugUeberstundenChange}
          />
        </Field>
        <Field
          label="Überstunden (Stand nach Abzug)"
          className={styles.fieldLabel}
        >
          <Input
            value={strUeberStunden}
            onChange={(e) => setStrUeberStunden(e.target.value)}
          />
        </Field>
        <Field label="Gleitzeitkonto" className={styles.fieldLabel}>
          <Input
            value={strGleitzeitKonto}
            onChange={(e) => setStrGleitzeitKonto(e.target.value)}
          />
        </Field>
        <Field label="Kommentar/Begründung" className={styles.fieldLabel}>
          <Textarea
            value={kommentar || ''}
            onChange={(e) => setKommentar(e.target.value)}
            maxLength={500}
          />
        </Field>
      </div>
      <br />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          appearance="primary"
          style={{ width: '250px' }}
          onClick={_sendClicked}
        >
          Speichern
        </Button>
        <Button style={{ width: '250px' }} onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </>
  );
};

export default EditMonth;
